import { appStore } from '@/store';
import { features } from '../../features';

/**
 * Pre Account Creation Errors errors are errors that occur during or before
 * the onboarding process that aren't a result of a 500 by GQL, in which case
 * we redirect to xoon or hermes
 */

// These errors are displayed on a particular field
const FIELD_LEVEL_ERRORS = {
  DOB_INVALID: {
    contentKey: 'fieldErrors.dobLegalAge',
    field: 'dob',
  },
  FIRST_NAME_MUST_BE_IN_ENGLISH: {
    content: 'fieldErrors.firstNameMustBeInEnglish',
    field: 'firstName',
  },
  INVALID_CITY: {
    contentKey: 'fieldErrors.cityInvalid',
    field: 'city',
  },
  INVALID_EMAIL_ID: {
    contentKey: 'fieldErrors.emailInvalid',
    field: 'email',
  },
  INVALID_FIRST_NAME: {
    contentKey: 'fieldErrors.firstNameInvalid',
    field: 'firstName',
  },
  INVALID_LAST_NAME: {
    contentKey: 'fieldErrors.lastNameInvalid',
    field: 'lastName',
  },
  INVALID_LINE1: {
    contentKey: 'fieldErrors.line1Invalid',
    field: 'line1',
  },
  INVALID_NATIONAL_ID: {
    contentKey: 'fieldErrors.identityDocumentNumberInvalid',
    field: 'identityDocumentNumber',
  },
  INVALID_NATIONAL_ID_TYPE: {
    contentKey: 'fieldErrors.identityDocumentTypeInvalid',
    field: 'identityDocumentType',
  },
  INVALID_PHONE_NUMBER: {
    contentKey: 'fieldErrors.phoneNumberInvalid',
    field: 'phone',
  },
  INVALID_PHONE_TYPE: {
    contentKey: '',
    field: 'phoneType',
  },
  INVALID_SECONDARY_ID: {
    contentKey: 'fieldErrors.secondaryIdentityDocumentNumberInvalid',
    field: 'secondaryIdentityDocumentNumber',
  },
  INVALID_SECONDARY_ID_TYPE: {
    contentKey: 'fieldErrors.secondaryIdentityDocumentTypeInvalid',
    field: 'secondaryIdentityDocumentType',
  },
  INVALID_STATE: {
    contentKey: 'fieldErrors.stateInvalid',
    field: 'state',
  },
  INVALID_ZIPCODE: {
    contentKey: 'fieldErrors.postalCodeInvalid',
    field: 'postalCode',
  },
  LAST_NAME_MUST_BE_IN_ENGLISH: {
    content: 'fieldErrors.lastNameMustBeInEnglish',
    field: 'lastName',
  },
  PASSWORD_TOO_LONG: {
    contentKey: 'fieldErrors.passwordLength',
    field: 'password',
  },
};

// These errors are displayed above the form
const PAGE_LEVEL_ERRORS = {
  ACCOUNT_ALREADY_EXISTS: () =>
    features.isActive('isAccountExistsErrorTrmt')
      ? 'pageLevelError.emailLinkedToFullAccount'
      : 'pageLevelError.accountExistsSignup',
  BANK_GENERIC_ERROR: () => 'pageLevelError.bankGenericError',
  BANK_LINKED_TO_FULL_ACCOUNT: () => 'pageLevelError.bankLinked',
  BIN_BLOCKED_OR_CARD_NOT_ALLOWED: () => 'pageLevelError.cardNotAllowed',
  CANNOT_PAY_SELF: () => 'pageLevelError.cardInternalError',
  CARD_GENERIC_ERROR: () => 'pageLevelError.cardGenericError',
  CC_INVALID_CSC: () => 'pageLevelError.invalidSecurityCode',
  CC_LINKED: () => 'pageLevelError.ccLinked',
  CC_LINKED_TO_FULL_ACCOUNT: () => 'pageLevelError.ccLinked',
  CHECK_DIGITS_REJECTED: () => 'pageLevelError.invalidSecurityCode',
  COUNTRY_NOT_SUPPORTED: () => 'pageLevelError.cardNotAllowed',
  CREATE_CARD_ACCOUNT_CANDIDATE_VALIDATION_ERROR() {
    return 'pageLevelError.cardGenericError';
  },
  EXPIRED_CARD: () => 'pageLevelError.expiredCard',
  EXPIRED_CREDIT_CARD: () => 'pageLevelError.expiredCard',
  INSTRUMENT_DISABLED: () => 'pageLevelError.cardDecline',
  INSTRUMENT_DUPLICATE_LIMIT_REACHED: () => 'pageLevelError.cardDecline',
  // INSTRUMENT_INTERNATIONAL: 'content.key',
  INSTRUMENT_REFUSED: () => 'pageLevelError.cardDecline',
  INSTRUMENT_REFUSED_DUE_TO_MAX_LIMIT: () => 'pageLevelError.cardDecline',
  INSTRUMENT_SHARING_LIMIT_EXCEEDED: () => 'pageLevelError.ccLinked',
  INTERNAL_ERROR: () => 'pageLevelError.cardInternalError',
  INVALID_CARD_NUMBER: () => 'pageLevelError.invalidCardNumOrType',
  INVALID_CARD_OR_TYPE: () => 'pageLevelError.invalidCardNumOrType',
  INVALID_CARD_TYPE: () => 'pageLevelError.invalidCardNumOrType',
  // INVALID_CITY_STATE_ZIPCODE: 'content.key',
  INVALID_EXPIRATION_DATE: () => 'pageLevelError.invalidExpiry',
  INVALID_EXPIRY: () => 'pageLevelError.invalidExpiry',
  INVALID_INSTRUMENT_ID: () => 'pageLevelError.invalidInstrumentId',
  INVALID_SECURITY_CODE: () => 'pageLevelError.invalidSecurityCode',
  ISSUER_REJECTED: () => 'pageLevelError.cardGenericError',
  ISSUER_RESTRICTED: () => 'pageLevelError.cardGenericError',
  MAX_AMOUNT_EXCEEDED: () => 'pageLevelError.maxAmoutExceeded',
  NEED_CREDIT_CARD: () => 'pageLevelError.cardDecline',
  NEED_CREDIT_CARD_OR_BANK_ACCOUNT: () => {
    const { ui: uiState } = appStore.getState();
    const { fiType } = uiState;

    if (fiType === 'bank') {
      return 'pageLevelError.cannotUseBank';
    }
    return 'pageLevelError.cardDecline';
  },
  // OAS_GENERIC_ERROR: 'content.key',
  REJECT_REFUSED: () => 'pageLevelError.cardGenericError',
  REJECT_UNKNOWN: () => 'pageLevelError.cardGenericError',
  RISK_DISALLOWED: () => 'pageLevelError.cardNotAllowed',
  SETTLEMENT_DENIED: () => 'pageLevelError.cardDecline',
  SHIPPING_REGION_UNSUPPORTED: () => 'pageLevelError.shippingRegionUnsupported',
  // TOO_MANY_REQUESTS: 'content.key',
};

/**
 * Testing to see what the impact on conversion is for handing the address
 * validation error on lite as opposed to falling back to xoon. Once we get a
 * read out and determine if the handling of this error on lite is successful
 * then we can remove this check and add the property to the object above.
 */
if (features.isActive('shouldHandleAddressValidationError')) {
  PAGE_LEVEL_ERRORS.VALIDATION_ERROR = () => 'pageLevelError.invalidAddress';
}

export { FIELD_LEVEL_ERRORS, PAGE_LEVEL_ERRORS };
